import React from 'react';
import clsx from 'clsx';
import { motion } from 'motion/react';
import { pluralize } from '../../../lib/pluralize';

type SeatProgressBarLabels = {
  active: (count: number) => string;
  available: (count: number) => string;
  childAllocated: (count: number) => string;
};

type SeatProgressBarProps = {
  /**
   * Total number of seats in the subscription
   */
  totalSeats: number;
  /**
   * Number of seats actively used by the agency (or parent agency in federated mode)
   */
  activeSeats: number;
  /**
   * Number of seats allocated to child agencies. Only provided for federated billing mode.
   * When provided, the progress bar will show three segments: active, child allocated, and available.
   */
  childAllocatedSeats?: number;
  /**
   * Size variant. 'sm' for compact version used in agency list items.
   */
  size?: 'sm' | 'lg';
  /**
   * Whether to show the legend below the progress bar
   */
  showLegend?: boolean;
  /**
   * Optional legend copy overrides. Defaults to seat-allocation wording used on
   * the subscription page; usage meters pass meter-specific labels.
   */
  labels?: Partial<SeatProgressBarLabels>;
};

const defaultLabels: SeatProgressBarLabels = {
  active: (count) => `${count} ${pluralize(count, 'seat')} assigned`,
  available: (count) => `${count} ${pluralize(count, 'seat')} available`,
  childAllocated: (count) => `${count} ${pluralize(count, 'seat')} allocated to subagencies`
};

/**
 * A progress bar showing seat allocation for a subscription.
 *
 * Supports two modes:
 * - Simple mode: Shows active vs available seats (sky blue vs neutral)
 * - Federated mode: Shows parent active, child allocated, and unallocated seats
 */
export const SeatProgressBar: React.FC<SeatProgressBarProps> = ({
  totalSeats,
  activeSeats,
  childAllocatedSeats,
  size = 'lg',
  showLegend = true,
  labels
}) => {
  const isFederatedMode = childAllocatedSeats !== undefined && childAllocatedSeats > 0;
  const availableSeats = totalSeats - activeSeats - (childAllocatedSeats ?? 0);
  const legend = { ...defaultLabels, ...labels };

  // Calculate percentages for each segment
  const denominator = totalSeats > 0 ? totalSeats : 1;
  const activePercent = Math.min(1, activeSeats / denominator) * 100;
  const childAllocatedPercent = isFederatedMode
    ? Math.min(1, (childAllocatedSeats ?? 0) / denominator) * 100
    : 0;

  // Determine if the bar should be fully rounded on the right
  const isFullyUsed = activeSeats + (childAllocatedSeats ?? 0) >= totalSeats;
  const isActiveOnly = !isFederatedMode && activeSeats >= totalSeats;

  const barHeight = size === 'sm' ? 'h-2' : 'h-4';
  const legendSize = size === 'sm' ? 'text-xs' : 'text-sm';
  const dotSize = size === 'sm' ? 'size-1.5' : 'size-2';

  return (
    <div className="w-full">
      {/* Progress bar container */}
      <div
        className={clsx(
          'w-full bg-neutral-100 dark:bg-neutral-700 rounded-md overflow-hidden flex',
          barHeight
        )}
      >
        {/* Active seats segment (sky) */}
        <motion.div
          animate={{ width: `${activePercent}%` }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className={clsx(
            'h-full bg-gradient-to-r from-sky-600 to-sky-500',
            activePercent > 0 && 'rounded-l-md',
            isActiveOnly && 'rounded-r-md'
          )}
        />

        {/* Child allocated seats segment (light sky) - only in federated mode */}
        {isFederatedMode && (
          <motion.div
            animate={{ width: `${childAllocatedPercent}%` }}
            transition={{ duration: 0.5, ease: 'easeInOut' }}
            className={clsx(
              'h-full bg-gradient-to-r from-sky-300 dark:from-sky-500/30 to-sky-200 dark:to-sky-500/25',
              activePercent === 0 && 'rounded-l-md',
              isFullyUsed && 'rounded-r-md'
            )}
          />
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div
          className={clsx(
            'flex flex-col sm:flex-row gap-4 sm:gap-8 mt-2',
            size === 'sm' && 'gap-2 sm:gap-4'
          )}
        >
          {/* Active seats */}
          <div className="flex items-center gap-2">
            <div className={clsx(dotSize, 'bg-sky-500 ring-1 ring-sky-600 rounded-full')} />
            <p className={clsx('text-neutral-500 dark:text-neutral-400', legendSize)}>
              {legend.active(activeSeats)}
            </p>
          </div>

          {/* Child allocated seats - only in federated mode */}
          {isFederatedMode && (
            <div className="flex items-center gap-2">
              <div
                className={clsx(
                  dotSize,
                  'bg-sky-300 dark:bg-sky-500/30 ring-1 ring-sky-400 rounded-full'
                )}
              />
              <p className={clsx('text-neutral-500 dark:text-neutral-400', legendSize)}>
                {legend.childAllocated(childAllocatedSeats ?? 0)}
              </p>
            </div>
          )}

          {/* Available seats */}
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                dotSize,
                'bg-neutral-100 dark:bg-neutral-700 ring-1 ring-neutral-200 dark:ring-neutral-700 rounded-full'
              )}
            />
            <p className={clsx('text-neutral-500 dark:text-neutral-400', legendSize)}>
              {legend.available(availableSeats)}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
